import { styled } from '@linaria/react';
import { CheckIcon, CloseButton, Combobox, InputBase, ScrollArea, useCombobox, VisuallyHidden } from '@mantine/core';
import {
  type CSSProperties,
  forwardRef,
  type KeyboardEvent,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { Task } from '../../../api/tasks-client/task-client.type';
import {
  buildTaskSelectOptions,
  CLEAR_RECENT_OPTION_VALUE,
  filterTaskSearchHistory,
  getTaskOptionLabel,
  isRecentOption,
  isResultOption,
} from './task-select.helpers';
import type { RecentTaskSelectOption, ResultTaskSelectOption } from './task-select.helpers';

type TaskSearchComboboxProps = {
  /**
   * Label the field shows while nothing is being typed; empty when no task is
   * selected. A parent that never turns a pick into a label (the field is a
   * launcher, not a selection) keeps this empty and sets `clearOnPick`.
   */
  selectedLabel: string;
  /** Id of the task to mark as selected in the list, if any. */
  selectedTaskId: string | null;
  /** Live search results for the current text. */
  results: Task[];
  /** Recent picks, most recent first. */
  recentEntries: Task[];
  /** Receives the typed text to search for; empty when the field only shows the selected label. */
  onSearchChange: (text: string) => void;
  /** Called with the task of the row the user chose, recent or result. */
  onPick: (task: Task) => void;
  /** Called with the recent pick the user removed, from its button or with Backspace/Delete. */
  onRemoveRecent: (task: Task) => void;
  /** Called when the user chooses the "Clear recent searches" row. */
  onClearRecent: () => void;
  /** When given, a clear button replaces the chevron while a task is selected. */
  onClear?: () => void;
  /**
   * Empties the field after a pick instead of showing the picked label. Pair it
   * with an empty `selectedLabel`; otherwise the picked label would sit in the
   * field as typed text and narrow the recent list to that one row.
   */
  clearOnPick?: boolean;
  /** Accessible name of the field. */
  fieldName: string;
  /** Placeholder shown while the field is empty. */
  placeholder: string;
  /** Accessible name of the suggestion list. */
  listName: string;
  style?: CSSProperties;
};

type TaskSearchComboboxRef = {
  /** Empties the field and the search text, as the clear button does. */
  reset: () => void;
};

/** Matches the height Mantine's `Select` gives its own dropdown. */
const DROPDOWN_MAX_HEIGHT = 220;

/** WCAG 2.2 minimum pointer target, in px, for the per-row remove button. */
const REMOVE_BUTTON_MIN_SIZE = 24;

const KEYBOARD_HINT =
  'Recent searches open when the field is focused. Press Backspace or Delete on a highlighted recent search to remove it.';

/**
 * Searchable task field whose dropdown holds the user's recent picks above the
 * live search results. The parent owns the search request and what a pick
 * does; this owns the field, the list and the keyboard contract.
 *
 * Responsibilities:
 * 1. Mirror the selected label in the field and hand typed text to the parent
 * 2. Show the recent picks on focus, narrowed as the user types, with a per-row
 *    remove button, a clear-all row, and Backspace/Delete removal of the highlighted row
 * 3. Report picks, removals and clears to the parent and announce them politely
 *
 * Dismissal follows the combobox pattern: Escape and a pick close the dropdown,
 * and so does blur. Rows and buttons call `preventDefault` on mousedown so a
 * click inside the dropdown never blurs the input first, which is what makes a
 * blur-delay unnecessary.
 *
 * Option ids are positional (`<prefix>-<index>`) on purpose. Mantine keeps the
 * `aria-activedescendant` id in state that only the arrow keys update, so after a
 * row is removed the stale id must still name a row that exists: with positional
 * ids it names whichever row slid into the freed slot, and the highlight is
 * re-applied to that same index once the list has re-rendered.
 *
 * @param {TaskSearchComboboxProps} props - Selection, results, history and the callbacks for each user action
 * @param {React.Ref<TaskSearchComboboxRef>} ref - Imperative handle exposing `reset`
 * @returns {JSX.Element} The field with its dropdown and live region
 */
export const TaskSearchCombobox = forwardRef<TaskSearchComboboxRef, TaskSearchComboboxProps>(
  (
    {
      selectedLabel,
      selectedTaskId,
      results,
      recentEntries,
      onSearchChange,
      onPick,
      onRemoveRecent,
      onClearRecent,
      onClear,
      clearOnPick = false,
      fieldName,
      placeholder,
      listName,
      style,
    },
    ref,
  ) => {
    const optionIdPrefix = useId();
    const [inputValue, setInputValue] = useState(selectedLabel);
    // Index to re-highlight once the list has re-rendered after a removal.
    const [pendingHighlightIndex, setPendingHighlightIndex] = useState<number | null>(null);
    // Polite announcement for screen readers after a removal or a clear.
    const [announcement, setAnnouncement] = useState('');

    // Latest search callback, read from the label-sync effect below. Keeping it
    // out of that effect's dependencies means a parent that re-creates the
    // callback each render cannot make the effect reset the field mid-typing.
    const onSearchChangeRef = useRef(onSearchChange);
    useEffect(() => {
      onSearchChangeRef.current = onSearchChange;
    });

    // The field shows the selected task's label; keep it in step when the
    // selection changes outside the field (reset filters, scope cascade, a pick).
    // The label is not a query, so the parent's search text is cleared with it.
    useEffect(() => {
      setInputValue(selectedLabel);
      onSearchChangeRef.current('');
    }, [selectedLabel]);

    const reset = useCallback(() => {
      setInputValue('');
      onSearchChange('');
    }, [onSearchChange]);

    useImperativeHandle(ref, () => ({ reset }), [reset]);

    // The selected label is displayed, not typed: it must not run as a query nor
    // narrow the recent list, otherwise focusing a filled field shows nothing.
    const filterText = inputValue === selectedLabel ? '' : inputValue;

    const recentMatches = useMemo(
      () => filterTaskSearchHistory(recentEntries, filterText),
      [recentEntries, filterText],
    );
    const options = useMemo(() => buildTaskSelectOptions(recentMatches, results), [recentMatches, results]);
    const recentOptions = options.filter(isRecentOption);
    const resultOptions = options.filter(isResultOption);
    const dropdownHidden = options.length === 0;
    const hasSelection = selectedLabel !== '';

    const combobox = useCombobox({
      onDropdownClose: () => combobox.resetSelectedOption(),
    });
    const { selectOption } = combobox;

    // Runs after the list has re-rendered without the removed row, so the DOM
    // query inside `selectOption` sees the new rows.
    useEffect(() => {
      if (pendingHighlightIndex === null) {
        return;
      }
      if (options.length > 0) {
        selectOption(Math.min(pendingHighlightIndex, options.length - 1));
      }
      setPendingHighlightIndex(null);
    }, [options, pendingHighlightIndex, selectOption]);

    const removeRecent = useCallback(
      (option: RecentTaskSelectOption, index: number) => {
        onRemoveRecent(option.task);
        setAnnouncement(`Removed ${getTaskOptionLabel(option.task)} from recent searches`);
        setPendingHighlightIndex(index);
      },
      [onRemoveRecent],
    );

    const handleOptionSubmit = (value: string) => {
      if (value === CLEAR_RECENT_OPTION_VALUE) {
        onClearRecent();
        setAnnouncement('Recent searches cleared');
        combobox.closeDropdown();
        return;
      }

      const option = options.find((candidate) => candidate.value === value);
      if (option && option.kind !== 'clear-recent') {
        onPick(option.task);
        setInputValue(clearOnPick ? '' : getTaskOptionLabel(option.task));
        onSearchChange('');
        combobox.closeDropdown();
      }
    };

    const handleClear = () => {
      reset();
      onClear?.();
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== 'Backspace' && event.key !== 'Delete') {
        return;
      }
      if (!combobox.dropdownOpened) {
        return;
      }
      const index = combobox.getSelectedOptionIndex();
      const highlighted = index >= 0 ? options[index] : undefined;
      if (highlighted && isRecentOption(highlighted)) {
        // A highlighted recent row is removed instead of editing the text.
        event.preventDefault();
        removeRecent(highlighted, index);
      }
    };

    const renderRecentOption = (option: RecentTaskSelectOption, index: number) => {
      const label = getTaskOptionLabel(option.task);
      const isSelected = option.task.id === selectedTaskId;
      return (
        <Combobox.Option
          key={option.value}
          id={`${optionIdPrefix}-${index}`}
          value={option.value}
          active={isSelected}
          aria-selected={isSelected}
        >
          <OptionRow>
            {isSelected && <CheckIcon size={12} aria-hidden="true" />}
            <OptionLabel title={label}>{label}</OptionLabel>
            {/* Mouse-only affordance: keyboard users remove a highlighted row with
              Backspace or Delete, so this stays out of the tab order and out of the
              option's accessible name, as Mantine's own clear button does. */}
            <CloseButton
              size="sm"
              tabIndex={-1}
              aria-hidden="true"
              title={`Remove ${label} from recent searches`}
              style={{ minWidth: REMOVE_BUTTON_MIN_SIZE, minHeight: REMOVE_BUTTON_MIN_SIZE }}
              onMouseDown={(event) => event.preventDefault()}
              onClick={(event) => {
                // Keep the click from reaching the row, which would pick the task.
                event.stopPropagation();
                removeRecent(option, index);
              }}
            />
          </OptionRow>
        </Combobox.Option>
      );
    };

    const renderResultOption = (option: ResultTaskSelectOption, index: number) => {
      const label = getTaskOptionLabel(option.task);
      const isSelected = option.task.id === selectedTaskId;
      return (
        <Combobox.Option
          key={option.value}
          id={`${optionIdPrefix}-${index}`}
          value={option.value}
          active={isSelected}
          aria-selected={isSelected}
        >
          <OptionRow>
            {isSelected && <CheckIcon size={12} aria-hidden="true" />}
            <OptionLabel title={label}>{label}</OptionLabel>
          </OptionRow>
        </Combobox.Option>
      );
    };

    // Row indexes must follow render order: recents, the clear row, then results.
    const clearRowIndex = recentOptions.length;
    const firstResultIndex = recentOptions.length > 0 ? clearRowIndex + 1 : 0;

    return (
      <>
        <Combobox store={combobox} onOptionSubmit={handleOptionSubmit} position="top">
          <Combobox.Target targetType="input" withExpandedAttribute autoComplete="off">
            <InputBase
              style={style}
              aria-label={fieldName}
              aria-autocomplete="list"
              description={KEYBOARD_HINT}
              descriptionProps={{ style: VISUALLY_HIDDEN_STYLE }}
              placeholder={placeholder}
              value={inputValue}
              radius="xl"
              rightSection={
                hasSelection && onClear ? <Combobox.ClearButton onClear={handleClear} /> : <Combobox.Chevron />
              }
              rightSectionPointerEvents={hasSelection && onClear ? 'all' : 'none'}
              onChange={(event) => {
                const next = event.currentTarget.value;
                setInputValue(next);
                onSearchChange(next === selectedLabel ? '' : next);
                combobox.openDropdown();
                combobox.resetSelectedOption();
              }}
              onFocus={() => combobox.openDropdown()}
              onClick={() => combobox.openDropdown()}
              onBlur={() => {
                combobox.closeDropdown();
                setInputValue(selectedLabel);
                onSearchChange('');
              }}
              onKeyDown={handleKeyDown}
            />
          </Combobox.Target>
          <Combobox.Dropdown hidden={dropdownHidden}>
            <Combobox.Options aria-label={listName}>
              <ScrollArea.Autosize mah={DROPDOWN_MAX_HEIGHT} type="scroll" scrollbars="y">
                {recentOptions.length > 0 && (
                  <Combobox.Group label="Recent searches">
                    {recentOptions.map((option, index) => renderRecentOption(option, index))}
                    <Combobox.Option id={`${optionIdPrefix}-${clearRowIndex}`} value={CLEAR_RECENT_OPTION_VALUE}>
                      <ClearRecentLabel>Clear recent searches</ClearRecentLabel>
                    </Combobox.Option>
                  </Combobox.Group>
                )}
                {resultOptions.map((option, index) => renderResultOption(option, firstResultIndex + index))}
              </ScrollArea.Autosize>
            </Combobox.Options>
          </Combobox.Dropdown>
        </Combobox>
        <VisuallyHidden aria-live="polite" aria-atomic="true">
          {announcement}
        </VisuallyHidden>
      </>
    );
  },
);

export type { TaskSearchComboboxRef };

const OptionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const OptionLabel = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--font-size-small);
`;

const ClearRecentLabel = styled.span`
  font-size: var(--font-size-small);
  color: var(--secondary-base);
`;

/**
 * Keeps the field's description for assistive technology only. Mantine wires
 * `aria-describedby` to its own `description` element (and drops a caller's
 * value), so the hint goes through that prop and is hidden with the standard
 * visually-hidden recipe instead of a layout-affecting element.
 */
const VISUALLY_HIDDEN_STYLE: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  margin: -1,
  padding: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};
